import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Text } from "src/library/components/Text";

import DraggableDatedProductsRow, {
  DraggableDatedProduct,
  UpdateProductListParams
} from "./DraggableDatedProductsRow";

export interface DraggableDatedProductsProps {
  datedProducts: DraggableDatedProduct[];
  lockSortableFeature?: boolean;
  isLoading?: boolean;
  disableInputs?: boolean;
}

/**
 * Renders a sortable list of products with dates
 */
const DraggableDatedProducts: React.ForwardRefRenderFunction<{currentDatedProducts: DraggableDatedProduct[]}, DraggableDatedProductsProps> = ({
  datedProducts, lockSortableFeature = false, isLoading = false, disableInputs = false
}, ref) => {
  const [ localDatedProducts, setLocalDatedProducts ] = useState(datedProducts);

  // prioritise prop if updates from consumer
  useEffect(() => {
    if (datedProducts) {
      setLocalDatedProducts(datedProducts);
    }
  }, [ datedProducts ]);

  // when a date is updated, update local state list
  const handleDateUpdates = useCallback((dateData: UpdateProductListParams) => {
    if (localDatedProducts) {
      setLocalDatedProducts(prevLocalDatedProducts => {
        return prevLocalDatedProducts.map(tag =>
          dateData.productId === tag.productId ? {
            ...tag,
            [ dateData.updateDateType ]: dateData.date
          } : tag
        );
      });
    }
  }, [ localDatedProducts ]);

  // when a row is deleted, update local state list
  const handleDeleteRow = useCallback((productId: number) => {
    // should never happen, but catching in case
    if (!productId) {
      toast.error("Unable to delete: product not found.");

      return;
    }

    if (localDatedProducts) {
      const filteredProducts = _.filter(localDatedProducts, datedProduct => datedProduct.productId !== productId);

      // reset priority on remaining items
      setLocalDatedProducts(_.map(filteredProducts, (datedProduct, index) => ({
        ...datedProduct,
        priority: index + 1
      })));
    }
  }, [ localDatedProducts ]);

  // update localDatedProducts sort orders when dragging a card
  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setLocalDatedProducts(prevDatedProducts => {
      if (dragIndex) {
        // hover and drag index - 1 to match array indexes as priority is never 0
        prevDatedProducts[ hoverIndex - 1 ].priority = dragIndex;
        prevDatedProducts[ dragIndex - 1 ].priority = hoverIndex;

        return _.orderBy(prevDatedProducts, datedProduct => datedProduct.priority);
      }

      return prevDatedProducts;
    });
  }, [ setLocalDatedProducts ]);

  // give access to local state for consumer
  useImperativeHandle(ref, () => ({ currentDatedProducts: localDatedProducts }), [ localDatedProducts ]);

  // if no data, don't bother rendering headers and list
  if (!localDatedProducts.length && !isLoading) {
    return (
      <Text>
        Currently no associated products.
      </Text>
    );
  }

  return (
    <ListWrapper lockSortableFeature={lockSortableFeature}>
      {localDatedProducts.length > 0 && (
        <>
          <div className="headers">
            <div className="icon-space" />

            <div className="header-items">
              <div className="header-item">
                <Text as="h4">
                  Product Name
                </Text>
              </div>

              <div className="header-item">
                <Text as="h4">
                  Part Number
                </Text>
              </div>

              <div className="header-item item-date">
                <Text as="h4">
                  Start Date
                </Text>
              </div>

              <div className="header-item item-date">
                <Text as="h4">
                  End Date
                </Text>
              </div>

              <div className="header-item" />
            </div>
          </div>

          <div className="product-associations">
            <DndProvider backend={HTML5Backend}>
              {localDatedProducts.map(datedProduct => (
                <DraggableDatedProductsRow
                  key={datedProduct.productId}
                  priority={datedProduct.priority || 1}
                  moveCard={moveCard}
                  datedProduct={datedProduct}
                  updateList={handleDateUpdates}
                  handleDeleteRow={handleDeleteRow}
                  lockSortableFeature={lockSortableFeature}
                  disableInputs={disableInputs}
                />
              ))}
            </DndProvider>
          </div>
        </>
      )}
    </ListWrapper>
  );
};

// controls matching width of header and detail items
export const ItemFlexFigures = {
  flexBasis: "19%",
  flexGrow: "1",
  minWidth: "875px"
};

const ListWrapper = styled.div<{ lockSortableFeature: boolean }>`
  ${({ theme, lockSortableFeature }) => css`
    padding-bottom: ${theme.spacing.padding.lg};

    .headers {
      display: ${!lockSortableFeature ? "-webkit-inline-box" : "flex"};
    }

    .icon-space {
      width: ${!lockSortableFeature ? "30px" : "0"};
    }

    .header-items {
      display: flex;
      flex-flow: row wrap;
      min-width: ${ItemFlexFigures.minWidth};
    }

    .header-item {
      flex-basis: ${ItemFlexFigures.flexBasis};
      flex-grow: ${ItemFlexFigures.flexGrow};
      padding: ${theme.spacing.padding.sm};
    }

    .item-date {
      display: flex;
      justify-content: center;
    }
  `}
`;

export default React.forwardRef(DraggableDatedProducts);
